import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Archive, Pencil, Plus } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import { FormularioGasto, type Gasto } from "@/paginas/interno/Gastos"

/**
 * Los servicios de un TIPO de proveedor (04/11, pedido del usuario): «Almacenaje» se crea
 * una vez en «Almacenadora» y no una por almacenadora. Cada proveedor del tipo marca en su
 * ficha los que presta, con su precio; el de aquí es el de REFERENCIA, para quien no tenga
 * uno propio.
 *
 * ⚠ **Son gastos del catálogo** con `tipo_proveedor_id` (`GET /gastos?tipo_proveedor_id=`):
 * la orden de pago los lleva en su línea igual que un gasto propio, y los informes los
 * agrupan por su categoría.
 *
 * ⚠ **Se ven con `gasto:ver` u `orden_pago:crear`** —el precio es lo que paga la agencia— y
 * se cambian con `gasto:administrar`. Sin poder verlos, la sección no se pinta.
 */
export function ServiciosDelTipo({ tipoId, tipoNombre }: { tipoId: number; tipoNombre: string }) {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeVer = tienePermiso(usuario, "gasto:ver") || tienePermiso(usuario, "orden_pago:crear")
  const puedeEditar = tienePermiso(usuario, "gasto:administrar")
  const [editando, setEditando] = useState<Gasto | "nuevo" | null>(null)
  const [archivando, setArchivando] = useState<number | null>(null)

  const servicios = useQuery({
    queryKey: ["gastos", "tipo", tipoId],
    queryFn: () =>
      api.get<Gasto[]>(`/api/v1/gastos?tipo_proveedor_id=${tipoId}&activo=true&limit=100`),
    enabled: puedeVer,
  })

  function refrescar() {
    clienteQuery.invalidateQueries({ queryKey: ["gastos"] })
    clienteQuery.invalidateQueries({ queryKey: ["proveedor-servicios"] })
  }

  const archivar = useMutation({
    mutationFn: (id: number) => api.patch(`/api/v1/gastos/${id}`, { activo: false }),
    onSuccess: () => {
      setArchivando(null)
      refrescar()
    },
  })

  if (!puedeVer) return null

  return (
    <section className="space-y-2" aria-label={`Servicios de ${tipoNombre}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Servicios de este tipo</h3>
        {puedeEditar && editando === null && (
          <Boton tamano="sm" onClick={() => setEditando("nuevo")}>
            <Plus size={14} aria-hidden={true} /> Agregar servicio
          </Boton>
        )}
      </div>
      <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
        Cada proveedor de este tipo marca en su ficha los que presta y su precio. El de aquí es el
        de referencia, para quien no tenga uno propio.
      </p>

      {editando !== null && (
        <div className="tarjeta p-3">
          <FormularioGasto
            key={editando === "nuevo" ? "nuevo" : editando.id}
            gasto={editando === "nuevo" ? null : editando}
            tipoFijo={{ id: tipoId, nombre: tipoNombre }}
            onCerrar={() => setEditando(null)}
            onGuardado={() => {
              setEditando(null)
              refrescar()
            }}
          />
        </div>
      )}

      {servicios.isPending && <EstadoCarga etiqueta="Cargando sus servicios…" filas={2} />}
      {servicios.isError && (
        <EstadoError
          titulo="No se pudieron cargar los servicios del tipo."
          error={servicios.error}
          onReintentar={() => servicios.refetch()}
        />
      )}
      {servicios.data && servicios.data.length === 0 && editando === null && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Todavía no tiene. El primero de una almacenadora suele ser «Almacenaje».
        </p>
      )}
      {servicios.data && servicios.data.length > 0 && (
        <ul className="space-y-1">
          {servicios.data.map((s) => (
            <li
              key={s.id}
              data-testid={`servicio-tipo-${s.id}`}
              className="tarjeta flex flex-wrap items-center gap-x-3 gap-y-1 p-3 text-sm"
            >
              <span className="min-w-0 flex-1">
                <span className="block font-medium break-words">{s.nombre}</span>
                <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                  {s.categoria ?? "Sin categoría"}
                </span>
              </span>
              <span className="text-right">
                <strong className="block tabular-nums">
                  {s.monto_referencia
                    ? `${monto(s.monto_referencia)} ${s.divisa ?? ""}`
                    : "Sin precio"}
                </strong>
                <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                  De referencia
                </span>
              </span>
              {puedeEditar &&
                editando === null &&
                (archivando === s.id ? (
                  <span className="flex items-center gap-1 text-xs">
                    ¿Archivar?
                    <Boton
                      tamano="sm"
                      variante="peligro"
                      disabled={archivar.isPending}
                      onClick={() => archivar.mutate(s.id)}
                    >
                      Sí
                    </Boton>
                    <Boton tamano="sm" onClick={() => setArchivando(null)}>
                      No
                    </Boton>
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <button
                      type="button"
                      aria-label={`Cambiar ${s.nombre}`}
                      onClick={() => setEditando(s)}
                      className="foco-cristal toque-cristal inline-flex size-8 items-center justify-center rounded-[var(--radius-control)]"
                    >
                      <Pencil size={14} aria-hidden={true} />
                    </button>
                    <button
                      type="button"
                      aria-label={`Archivar ${s.nombre}`}
                      onClick={() => setArchivando(s.id)}
                      className="foco-cristal toque-cristal inline-flex size-8 items-center justify-center rounded-[var(--radius-control)]"
                    >
                      <Archive size={14} aria-hidden={true} />
                    </button>
                  </span>
                ))}
            </li>
          ))}
        </ul>
      )}
      {archivar.error instanceof ErrorApi && (
        <p className="text-sm" style={{ color: "var(--destructive)" }}>
          {archivar.error.message}
        </p>
      )}
    </section>
  )
}
